import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import ChoiceCard from './ChoiceCard';

/**
 * Epic Choice Cards Modal
 * Full-screen modal with dramatic entrance animations for focused decision making
 * Preserves context while eliminating distractions
 */
const ChoiceCardsModal = ({ 
  isOpen = false, 
  cards = [], 
  preferredChoice = null,
  preferenceReasoning = "",
  onCardSelect = () => {},
  onClose = () => {},
  darkMode = false 
}) => {
  const [animationPhase, setAnimationPhase] = useState('hidden'); // hidden -> analyzing -> revealing -> complete
  const [showContent, setShowContent] = useState(false);
  const [showRecommendation, setShowRecommendation] = useState(false);

  // Epic entrance sequence
  useEffect(() => {
    if (isOpen) {
      // Phase 1: Overlay appears + Analysis message
      setAnimationPhase('analyzing');
      
      setTimeout(() => {
        // Phase 2: Cards start materializing
        setAnimationPhase('revealing');
        setShowContent(true);
        
        setTimeout(() => {
          // Phase 3: Recommendation appears
          setShowRecommendation(true);
          
          setTimeout(() => {
            // Phase 4: Complete - all animations done
            setAnimationPhase('complete');
          }, 800);
        }, 1200);
      }, 2000);
    } else {
      // Reset for next time
      setAnimationPhase('hidden');
      setShowContent(false);
      setShowRecommendation(false);
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden'; // Prevent background scroll
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, onClose]);

  const handleCardClick = (card) => {
    onCardSelect(card);
    onClose();
  };

  const handleKeepDigging = () => {
    onClose();
  };

  if (!isOpen) return null;

  // Render modal using Portal to escape component hierarchy and appear above everything
  return createPortal(
    <>
      {/* Modal Overlay with Epic Animation */}
      <div 
        className={`
          fixed inset-0 z-[9999] flex items-center justify-center p-4
          transition-all duration-500 ease-out
          ${animationPhase === 'hidden' 
            ? 'opacity-0 pointer-events-none' 
            : 'opacity-100'
          }
        `}
        style={{
          background: animationPhase === 'analyzing' 
            ? 'rgba(0, 0, 0, 0.3)' 
            : 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(8px)',
          transition: 'all 1s ease-out'
        }}
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        
        {/* Analysis Phase - Loading Message */}
        {animationPhase === 'analyzing' && (
          <div className={`
            absolute inset-0 flex items-center justify-center
            transition-opacity duration-1000
            ${animationPhase === 'analyzing' ? 'opacity-100' : 'opacity-0'}
          `}>
            <div className={`
              text-center space-y-6 transform transition-all duration-1000
              ${darkMode ? 'text-white' : 'text-white'}
            `}>
              <div className="text-2xl font-bold">
                ✨ Analyzing your opportunities...
              </div>
              
              {/* Dancing Sparkles Animation */}
              <div className="relative flex items-center justify-center h-16">
                {/* Center sparkle cluster */}
                <div className="relative">
                  <div className="text-4xl animate-sparkle-dance">✨</div>
                  <div className="text-2xl animate-sparkle-dance-delayed absolute -top-2 -left-4">⭐</div>
                  <div className="text-3xl animate-sparkle-dance-slow absolute -bottom-1 -right-3">💫</div>
                </div>
                
                {/* Orbiting sparkles */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-xl animate-sparkle-orbit">✨</div>
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-lg animate-sparkle-orbit-reverse">⭐</div>
                </div>
                
                {/* Floating sparkles */}
                <div className="text-sm animate-sparkle-float absolute top-2 left-8">✨</div>
                <div className="text-xs animate-sparkle-float-delayed absolute bottom-4 right-6">⭐</div>
                <div className="text-sm animate-sparkle-float-slow absolute top-6 right-4">💫</div>
                <div className="text-xs animate-sparkle-float-reverse absolute bottom-2 left-6">✨</div>
              </div>
              
              <div className="text-lg text-white/80">
                Finding your highest-impact wins...
              </div>
            </div>
          </div>
        )}

        {/* Discovery Phase - Cards Reveal */}
        {(animationPhase === 'revealing' || animationPhase === 'complete') && (
          <div className={`
            w-full max-w-7xl mx-auto transform transition-all duration-1000 ease-out
            ${showContent 
              ? 'opacity-100 scale-100 translate-y-0' 
              : 'opacity-0 scale-95 translate-y-8'
            }
          `}>
            
            {/* Header with Discovery Message */}
            <div className="text-center mb-8">
              <div className={`
                text-3xl font-bold mb-2 transition-all duration-800
                ${darkMode ? 'text-white' : 'text-white'}
                ${showContent ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}
              `}>
                🎯 Your Strategic Opportunities
              </div>
              <div className={`
                text-xl text-white/90 transition-all duration-800 delay-300
                ${showContent ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}
              `}>
                I've identified your highest-impact wins
              </div>
            </div>

            {/* Choice Cards Grid */}
            <div className={`
              grid gap-6 mb-8
              ${cards.length === 1 
                ? 'grid-cols-1 max-w-md mx-auto' 
                : cards.length === 2 
                  ? 'grid-cols-1 md:grid-cols-2 max-w-4xl mx-auto' 
                  : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
              }
            `}>
              {cards.map((card, index) => {
                const isPreferred = card.id === preferredChoice;
                return (
                  <div
                    key={card.id}
                    className={`
                      transform transition-all duration-700 ease-out
                      ${showContent 
                        ? 'opacity-100 scale-100 translate-y-0' 
                        : 'opacity-0 scale-95 translate-y-8'
                      }
                    `}
                    style={{
                      transitionDelay: `${400 + (index * 200)}ms`
                    }}
                  >
                    <ChoiceCard
                      title={card.title}
                      summary={card.summary}
                      buttonText={card.button_text || (card.tutorial_context ? 'Start Tutorial' : 'Analyze This')}
                      onClick={() => handleCardClick(card)}
                      darkMode={false} // Always light in modal for contrast
                      index={index}
                      isVisible={showContent}
                      isPreferred={isPreferred}
                      quickWin={card.quick_win}
                      actionPayload={card.action_payload}
                      showLetterBadge={true}
                      // Tutorial-specific metadata
                      difficulty={card.difficulty}
                      estimatedTime={card.estimated_time}
                      tutorialContext={card.tutorial_context}
                    />
                  </div>
                );
              })}
            </div>

            {/* Agent Recommendation Section */}
            {preferredChoice && preferenceReasoning && (
              <div className={`
                max-w-2xl mx-auto mb-8 transform transition-all duration-800 ease-out
                ${showRecommendation 
                  ? 'opacity-100 scale-100 translate-y-0' 
                  : 'opacity-0 scale-95 translate-y-8'
                }
              `}>
                <div className={`
                  p-6 rounded-2xl backdrop-blur-sm border-2
                  bg-white/95 border-blue-200/50 shadow-2xl
                `}>
                  <div className="text-center space-y-4">
                    <h3 className="text-xl font-bold text-gray-900 flex items-center justify-center gap-2">
                      💡 My Recommendation
                    </h3>
                    
                    <p className="text-gray-700 leading-relaxed">
                      {preferenceReasoning}
                    </p>
                    
                    <button
                      onClick={() => {
                        const recommendedCard = cards.find(card => card.id === preferredChoice);
                        if (recommendedCard) handleCardClick(recommendedCard);
                      }}
                      className={`
                        px-6 py-3 rounded-lg font-semibold text-white
                        bg-gradient-to-r from-blue-500 to-purple-600 
                        hover:from-blue-600 hover:to-purple-700
                        transform hover:scale-105 transition-all duration-200
                        shadow-lg hover:shadow-xl
                      `}
                    >
                      Let's do it 🚀
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Bottom Actions */}
            <div className={`
              text-center transform transition-all duration-800 ease-out
              ${showRecommendation 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-4'
              }
            `}>
              <button
                onClick={handleKeepDigging}
                className={`
                  px-6 py-3 rounded-lg font-medium transition-all duration-200
                  text-white/80 hover:text-white 
                  hover:bg-white/10 backdrop-blur-sm
                  border border-white/20 hover:border-white/40
                `}
              >
                Keep Digging in the Data
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Custom CSS for dramatic animations */}
      <style jsx>{`
        @keyframes modal-entrance {
          0% {
            opacity: 0;
            transform: scale(0.9) translateY(20px);
          }
          100% {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        
        @keyframes sparkle-dance {
          0%, 100% { 
            transform: scale(1) rotate(0deg) translateY(0px);
            opacity: 1;
          }
          25% { 
            transform: scale(1.2) rotate(5deg) translateY(-3px);
            opacity: 0.8;
          }
          50% { 
            transform: scale(0.9) rotate(-3deg) translateY(2px);
            opacity: 1;
          }
          75% { 
            transform: scale(1.1) rotate(2deg) translateY(-1px);
            opacity: 0.9;
          }
        }
        
        @keyframes sparkle-orbit {
          0% { 
            transform: rotate(0deg) translateX(40px) rotate(0deg);
          }
          100% { 
            transform: rotate(360deg) translateX(40px) rotate(-360deg);
          }
        }
        
        @keyframes sparkle-orbit-reverse {
          0% { 
            transform: rotate(0deg) translateX(30px) rotate(0deg);
          }
          100% { 
            transform: rotate(-360deg) translateX(30px) rotate(360deg);
          }
        }
        
        @keyframes sparkle-float {
          0%, 100% { 
            transform: translateY(0px) scale(1);
            opacity: 0.7;
          }
          50% { 
            transform: translateY(-8px) scale(1.1);
            opacity: 1;
          }
        }
        
        .modal-entrance {
          animation: modal-entrance 0.6s ease-out forwards;
        }
        
        .animate-sparkle-dance {
          animation: sparkle-dance 1.5s ease-in-out infinite;
        }
        
        .animate-sparkle-dance-delayed {
          animation: sparkle-dance 1.5s ease-in-out infinite;
          animation-delay: 0.3s;
        }
        
        .animate-sparkle-dance-slow {
          animation: sparkle-dance 2s ease-in-out infinite;
          animation-delay: 0.6s;
        }
        
        .animate-sparkle-orbit {
          animation: sparkle-orbit 3s linear infinite;
        }
        
        .animate-sparkle-orbit-reverse {
          animation: sparkle-orbit-reverse 4s linear infinite;
        }
        
        .animate-sparkle-float {
          animation: sparkle-float 2s ease-in-out infinite;
        }
        
        .animate-sparkle-float-delayed {
          animation: sparkle-float 2.5s ease-in-out infinite;
          animation-delay: 0.5s;
        }
        
        .animate-sparkle-float-slow {
          animation: sparkle-float 3s ease-in-out infinite;
          animation-delay: 1s;
        }
        
        .animate-sparkle-float-reverse {
          animation: sparkle-float 2.2s ease-in-out infinite;
          animation-delay: 1.5s;
        }
      `}</style>
    </>,
    document.body // Portal target - renders at body level, above all other UI
  );
};

export default ChoiceCardsModal;
